import React, { useState, useCallback, useRef, useEffect } from 'react';
import Form from '../Form/Form';
import FormTitle from '../FormTitle/FormTitle';
import FormSubtitle from '../FormSubtitle/FormSubtitle';
import FormDescription from '../FormDescription/FormDescription';
import { TextInput, Textarea, Select, FormSection, Checkbox } from 'feSharedIndex';
import FormGroup from '../FormGroup/FormGroup';
import FormActions from '../FormActions/FormActions';
import Radio from '../Radio/Radio';
import Toggle from '../Toggle/Toggle';
import NumberInput from '../NumberInput/NumberInput';
import DateInput from '../DateInput/DateInput';
import FileInput from '../FileInput/FileInput';
import PrimaryButton from '../../button/PrimaryButton';
import SecondaryButton from '../../button/SecondaryButton';
import Modal from '../../ui/Modal/Modal';
import './FormBuilder.css';

/**
 * FormBuilder Component
 * Interactive form builder for creating forms dynamically with drag-and-drop
 * 
 * @param {string} className - Additional CSS classes
 */
const FormBuilder = ({ className = '' }) => {
  const [formItems, setFormItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [resizingIndex, setResizingIndex] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(null);
  const [resizeStartSpan, setResizeStartSpan] = useState(null);
  const [showGridOverlay, setShowGridOverlay] = useState(false);
  const previewRef = useRef(null);
  const gridRef = useRef(null);

  // Component import paths mapping
  const componentImportPaths = {
    FormTitle: '../../components/form/FormTitle/FormTitle',
    FormSubtitle: '../../components/form/FormSubtitle/FormSubtitle',
    FormDescription: '../../components/form/FormDescription/FormDescription',
    FormSection: '../../components/form/FormSection/FormSection',
    FormGroup: '../../components/form/FormGroup/FormGroup',
    FormActions: '../../components/form/FormActions/FormActions',
    TextInput: '../../components/form/TextInput/TextInput',
    Textarea: '../../components/form/Textarea/Textarea',
    Select: '../../components/form/Select/Select',
    Checkbox: '../../components/form/Checkbox/Checkbox',
    Radio: '../../components/form/Radio/Radio',
    Toggle: '../../components/form/Toggle/Toggle',
    NumberInput: '../../components/form/NumberInput/NumberInput',
    DateInput: '../../components/form/DateInput/DateInput',
    FileInput: '../../components/form/FileInput/FileInput'
  };

  // Available component types
  const componentTypes = [
    { type: 'FormTitle', label: 'Form Title', icon: 'H1' },
    { type: 'FormSubtitle', label: 'Form Subtitle', icon: 'H3' },
    { type: 'FormDescription', label: 'Form Description', icon: 'P' },
    { type: 'FormSection', label: 'Form Section', icon: '[]' },
    { type: 'TextInput', label: 'Text Input', icon: 'T' },
    { type: 'Textarea', label: 'Textarea', icon: 'TA' },
    { type: 'Select', label: 'Select', icon: '▼' },
    { type: 'Checkbox', label: 'Checkbox', icon: '☐' },
    { type: 'Radio', label: 'Radio', icon: '○' },
    { type: 'Toggle', label: 'Toggle', icon: '◄►' },
    { type: 'NumberInput', label: 'Number Input', icon: '123' },
    { type: 'DateInput', label: 'Date Input', icon: 'D' },
    { type: 'FileInput', label: 'File Input', icon: 'F' },
    { type: 'FormGroup', label: 'Form Group', icon: '{}' },
    { type: 'FormActions', label: 'Form Actions', icon: '→' }
  ];

  // Get default props for a component type
  const getDefaultProps = (type) => {
    const defaults = {
      FormTitle: { text: 'Form Title', level: 1 },
      FormSubtitle: { text: 'Form Subtitle', level: 3 },
      FormDescription: { text: 'Form description text', variant: 'default' },
      FormSection: { title: 'Section Title', description: 'Section description' },
      TextInput: { label: 'Text Input', placeholder: 'Enter text...', type: 'text' },
      Textarea: { label: 'Textarea', placeholder: 'Enter text...', rows: 4 },
      Select: { label: 'Select', placeholder: 'Choose an option', options: [{ value: '1', label: 'Option 1' }] },
      Checkbox: { label: 'Checkbox Option' },
      Radio: { label: 'Radio Group', name: 'radio-group', options: [{ value: '1', label: 'Option 1' }] },
      Toggle: { label: 'Toggle Option', description: 'Toggle description' },
      NumberInput: { label: 'Number Input', value: 0, min: 0, max: 100, step: 1 },
      DateInput: { label: 'Date Input' },
      FileInput: { label: 'File Input', accept: '*/*' },
      FormGroup: { label: 'Form Group' },
      FormActions: { primaryLabel: 'Submit', secondaryLabel: 'Cancel' }
    };
    return defaults[type] || {};
  };

  // Add a new component to the form
  const handleAddComponent = (type) => {
    const newItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      props: getDefaultProps(type),
      gridColumn: 'span 12', // Default: full width
      gridRow: 'auto', // Auto row placement
      gridPosition: { start: 1, end: 13, span: 12 } // Track grid position
    };
    setFormItems([...formItems, newItem]);
    setSelectedItemIndex(formItems.length);
  };

  // Remove a component from the form
  const handleRemoveComponent = (index) => {
    const newItems = formItems.filter((_, i) => i !== index);
    setFormItems(newItems);
    if (selectedItemIndex === index) {
      setSelectedItemIndex(null);
    } else if (selectedItemIndex > index) {
      setSelectedItemIndex(selectedItemIndex - 1);
    }
  };

  // Update component props
  const handleUpdateProps = (index, newProps) => {
    const newItems = [...formItems];
    newItems[index] = {
      ...newItems[index],
      props: { ...newItems[index].props, ...newProps }
    };
    setFormItems(newItems);
  };

  // Update grid position
  const handleUpdateGridPosition = (index, gridColumn, gridRow, gridPosition) => {
    const newItems = [...formItems];
    newItems[index] = {
      ...newItems[index],
      gridColumn,
      gridRow,
      gridPosition
    };
    setFormItems(newItems);
  };

  // Handle resize start
  const handleResizeStart = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingIndex(index);
    setResizeStartX(e.clientX);
    const item = formItems[index];
    setResizeStartSpan(item?.gridPosition?.span || 12);
    setShowGridOverlay(true);
  };

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (resizingIndex === null || resizeStartX === null || resizeStartSpan === null) return;
    
    const gridElement = gridRef.current;
    if (!gridElement) return;
    
    const gridRect = gridElement.getBoundingClientRect();
    const gridWidth = gridRect.width;
    const columnWidth = gridWidth / 12;
    const deltaX = e.clientX - resizeStartX;
    const deltaColumns = Math.round(deltaX / columnWidth);
    const newSpan = Math.max(1, Math.min(12, resizeStartSpan + deltaColumns));
    
    setFormItems(prevItems => {
      const newItems = [...prevItems];
      const item = newItems[resizingIndex];
      if (!item) return prevItems;
      
      const newStart = item.gridPosition?.start || 1;
      const newEnd = newStart + newSpan;
      const newGridColumn = `span ${newSpan}`;
      
      newItems[resizingIndex] = {
        ...item,
        gridColumn: newGridColumn,
        gridPosition: { start: newStart, end: newEnd, span: newSpan }
      };
      
      return newItems;
    });
  }, [resizingIndex, resizeStartX, resizeStartSpan]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setResizingIndex(null);
    setResizeStartX(null);
    setResizeStartSpan(null);
    setShowGridOverlay(false);
  }, []);

  // Add resize event listeners
  useEffect(() => {
    if (resizingIndex !== null) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingIndex, handleResizeMove, handleResizeEnd]);

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    setShowGridOverlay(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null) return;
    
    // Calculate target grid cell
    const gridElement = gridRef.current;
    if (gridElement) {
      const gridRect = gridElement.getBoundingClientRect();
      const gridWidth = gridRect.width;
      const columnWidth = gridWidth / 12;
      const relativeX = e.clientX - gridRect.left;
      const targetColumn = Math.max(1, Math.min(12, Math.floor(relativeX / columnWidth) + 1));
      
      const draggedItem = formItems[draggedIndex];
      const currentSpan = draggedItem.gridPosition?.span || 12;
      const newStart = Math.max(1, Math.min(13 - currentSpan, targetColumn));
      // eslint-disable-next-line no-unused-vars
      const newEnd = newStart + currentSpan;
      
      setDragOverIndex(index);
    } else {
      if (draggedIndex !== null && draggedIndex !== index) {
        setDragOverIndex(index);
      }
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setShowGridOverlay(false);
      return;
    }

    // Calculate target grid cell
    const gridElement = gridRef.current;
    let newStart = formItems[draggedIndex].gridPosition?.start || 1;
    
    if (gridElement) {
      const gridRect = gridElement.getBoundingClientRect();
      const gridWidth = gridRect.width;
      const columnWidth = gridWidth / 12;
      const relativeX = e.clientX - gridRect.left;
      const targetColumn = Math.max(1, Math.min(12, Math.floor(relativeX / columnWidth) + 1));
      
      const draggedItem = formItems[draggedIndex];
      const currentSpan = draggedItem.gridPosition?.span || 12;
      newStart = Math.max(1, Math.min(13 - currentSpan, targetColumn));
    }

    const draggedItem = formItems[draggedIndex];
    const currentSpan = draggedItem.gridPosition?.span || 12;
    const newEnd = newStart + currentSpan;
    const newGridColumn = `${newStart} / ${newEnd}`;

    // Update grid position
    const newItems = [...formItems];
    newItems[draggedIndex] = {
      ...draggedItem,
      gridColumn: newGridColumn,
      gridPosition: { start: newStart, end: newEnd, span: currentSpan }
    };
    
    setFormItems(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setShowGridOverlay(false);
  };

  // Render a form component based on type
  const renderComponent = (item, index) => {
    const { type, props } = item;
    const commonProps = {
      key: item.id,
      ...props
    };

    switch (type) {
      case 'FormTitle':
        return <FormTitle {...commonProps} />;
      case 'FormSubtitle':
        return <FormSubtitle {...commonProps} />;
      case 'FormDescription':
        return <FormDescription {...commonProps} />;
      case 'FormSection':
        // Find children items that belong to this section
        // eslint-disable-next-line no-unused-vars
        const sectionChildren = formItems.filter((item, idx) => {
          // For now, just render placeholder - in a real implementation, you'd track parent-child relationships
          return false;
        });
        return <FormSection {...commonProps}><div style={{ padding: '8px 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Section content - add components below</div></FormSection>;
      case 'TextInput':
        return (
          <TextInput
            {...commonProps}
            value={formValues[`text-${index}`] || ''}
            onChange={(value) => setFormValues({ ...formValues, [`text-${index}`]: value })}
          />
        );
      case 'Textarea':
        return (
          <Textarea
            {...commonProps}
            value={formValues[`textarea-${index}`] || ''}
            onChange={(value) => setFormValues({ ...formValues, [`textarea-${index}`]: value })}
          />
        );
      case 'Select':
        return (
          <Select
            {...commonProps}
            value={formValues[`select-${index}`] || ''}
            onChange={(value) => setFormValues({ ...formValues, [`select-${index}`]: value })}
          />
        );
      case 'Checkbox':
        return (
          <Checkbox
            {...commonProps}
            checked={formValues[`checkbox-${index}`] || false}
            onChange={(checked) => setFormValues({ ...formValues, [`checkbox-${index}`]: checked })}
          />
        );
      case 'Radio':
        return (
          <Radio
            {...commonProps}
            value={formValues[`radio-${index}`] || ''}
            onChange={(value) => setFormValues({ ...formValues, [`radio-${index}`]: value })}
          />
        );
      case 'Toggle':
        return (
          <Toggle
            {...commonProps}
            checked={formValues[`toggle-${index}`] || false}
            onChange={(checked) => setFormValues({ ...formValues, [`toggle-${index}`]: checked })}
          />
        );
      case 'NumberInput':
        return (
          <NumberInput
            {...commonProps}
            value={formValues[`number-${index}`] || 0}
            onChange={(value) => setFormValues({ ...formValues, [`number-${index}`]: value })}
          />
        );
      case 'DateInput':
        return (
          <DateInput
            {...commonProps}
            value={formValues[`date-${index}`] || ''}
            onChange={(value) => setFormValues({ ...formValues, [`date-${index}`]: value })}
          />
        );
      case 'FileInput':
        return (
          <FileInput
            {...commonProps}
            onChange={(files) => setFormValues({ ...formValues, [`file-${index}`]: files })}
          />
        );
      case 'FormGroup':
        return <FormGroup {...commonProps}><div>Group content goes here</div></FormGroup>;
      case 'FormActions':
        return (
          <FormActions
            {...commonProps}
            onPrimary={() => console.log('Primary action', formValues)}
            onSecondary={() => console.log('Secondary action')}
          />
        );
      default:
        return null;
    }
  };

  // Export form configuration as JSON with component references
  const handleExport = () => {
    const uniqueTypes = [...new Set(formItems.map(item => item.type))];
    const imports = uniqueTypes.map(type => ({
      type,
      importPath: componentImportPaths[type] || `../../components/form/${type}/${type}`
    }));
    
    // eslint-disable-next-line no-unused-vars
    const formatPropValue = (value) => {
      if (typeof value === 'string') return `"${value}"`;
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      if (typeof value === 'number') return String(value);
      if (Array.isArray(value)) return JSON.stringify(value);
      if (typeof value === 'object' && value !== null) return JSON.stringify(value);
      return 'null';
    };

    const formatProps = (props) => {
      return Object.entries(props)
        .filter(([_, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => {
          if (typeof value === 'string') return `${key}="${value}"`;
          if (typeof value === 'boolean') return value ? `${key}={true}` : '';
          if (typeof value === 'number') return `${key}={${value}}`;
          if (Array.isArray(value)) return `${key}={${JSON.stringify(value)}}`;
          if (typeof value === 'object' && value !== null) return `${key}={${JSON.stringify(value)}}`;
          return '';
        })
        .filter(Boolean)
        .join(' ');
    };

    const usageCode = `// Form implementation
import React from 'react';
${imports.map(imp => `import ${imp.type} from '${imp.importPath}';`).join('\n')}
import Form from 'components/form/Form/Form';

const MyForm = () => {
  return (
    <Form onSubmit={(values) => console.log('Form submitted:', values)}>
${formItems.map(item => {
  const propsStr = formatProps(item.props);
  return `      <${item.type}${propsStr ? ' ' + propsStr : ''} />`;
}).join('\n')}
    </Form>
  );
};

export default MyForm;`;
    
    const config = {
      formConfig: {
        items: formItems.map(item => ({
          type: item.type,
          importPath: componentImportPaths[item.type] || `../../components/form/${item.type}/${item.type}`,
          props: item.props,
          gridColumn: item.gridColumn || 'span 12',
          gridRow: item.gridRow || 'auto',
          gridPosition: item.gridPosition || { start: 1, end: 13, span: 12 }
        }))
      },
      imports: imports.map(imp => ({
        type: imp.type,
        importPath: imp.importPath,
        importStatement: `import ${imp.type} from '${imp.importPath}';`
      })),
      usage: usageCode
    };
    
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'form-config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clear form with modal confirmation
  const handleClear = () => {
    setShowClearModal(true);
  };

  const confirmClear = () => {
    setFormItems([]);
    setSelectedItemIndex(null);
    setFormValues({});
    setShowClearModal(false);
  };

  // Prevent random scrolling
  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.scrollTop = 0;
    }
  }, [formItems.length]);

  const selectedItem = selectedItemIndex !== null ? formItems[selectedItemIndex] : null;

  return (
    <div className={`form-builder ${className}`.trim()}>
      <div className="form-builder-header">
        <h3>Form Builder</h3>
        <div className="form-builder-actions">
          <SecondaryButton onClick={handleClear} size="small">
            Clear
          </SecondaryButton>
          <PrimaryButton onClick={handleExport} size="small" disabled={formItems.length === 0}>
            Export JSON
          </PrimaryButton>
        </div>
      </div>

      <div className="form-builder-content">
        {/* Component Palette */}
        <div className="form-builder-palette">
          <h4>Add Components</h4>
          <div className="form-builder-palette-grid">
            {componentTypes.map((comp) => (
              <button
                key={comp.type}
                className="form-builder-palette-item"
                onClick={() => handleAddComponent(comp.type)}
                title={comp.label}
              >
                <span className="form-builder-palette-icon">{comp.icon}</span>
                <span className="form-builder-palette-label">{comp.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Form Preview */}
        <div className="form-builder-preview" ref={previewRef}>
          <h4>Form Preview</h4>
          <div className="form-builder-preview-content">
            {formItems.length === 0 ? (
              <div className="form-builder-empty">
                <p>No components added yet. Click on a component type above to add it to your form.</p>
              </div>
            ) : (
              <div className="form-builder-grid" ref={gridRef}>
                {showGridOverlay && (
                  <div className="form-builder-grid-overlay">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="form-builder-grid-guide" />
                    ))}
                  </div>
                )}
                <Form onSubmit={(values) => console.log('Form submitted:', values)}>
                  {formItems.map((item, index) => (
                    <div
                      key={item.id}
                      className={`form-builder-item-wrapper ${selectedItemIndex === index ? 'selected' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${draggedIndex === index ? 'dragging' : ''} ${resizingIndex === index ? 'resizing' : ''}`}
                      style={{
                        gridColumn: item.gridColumn || 'span 12',
                        gridRow: item.gridRow || 'auto'
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onClick={() => setSelectedItemIndex(index)}
                    >
                      <div className="form-builder-item-handle">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          <path d="M9 5h2v2H9V5zm0 6h2v2H9v-2zm0 6h2v2H9v-2zm4-12h2v2h-2V5zm0 6h2v2h-2v-2zm0 6h2v2h-2v-2z"/>
                        </svg>
                        <span>{item.type}</span>
                        <span className="form-builder-item-span-indicator">
                          {item.gridPosition?.span || 12} cols
                        </span>
                      </div>
                      <div className="form-builder-item-content">
                        {renderComponent(item, index)}
                      </div>
                      <div className="form-builder-item-resize-handle"
                        onMouseDown={(e) => handleResizeStart(e, index)}
                        title="Resize"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                          <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18ZM14 22H12V20H14V22ZM22 14H20V12H22V14Z"/>
                        </svg>
                      </div>
                      <div className="form-builder-item-actions">
                        <button
                          className="form-builder-item-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveComponent(index);
                          }}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </Form>
              </div>
            )}
          </div>
        </div>

        {/* Property Editor */}
        {selectedItem && (
          <div className="form-builder-properties">
            <h4>Edit Properties</h4>
            <div className="form-builder-properties-content">
              {/* Grid Position Controls */}
              <div className="form-builder-grid-controls">
                <h5>Grid Layout</h5>
                <div className="form-builder-grid-control-group">
                  <label>Column Span (1-12):</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={selectedItem.gridPosition?.span || 12}
                    onChange={(e) => {
                      const newSpan = Math.max(1, Math.min(12, parseInt(e.target.value) || 1));
                      const currentStart = selectedItem.gridPosition?.start || 1;
                      const newEnd = currentStart + newSpan;
                      const newGridColumn = `span ${newSpan}`;
                      handleUpdateGridPosition(
                        selectedItemIndex,
                        newGridColumn,
                        selectedItem.gridRow || 'auto',
                        { start: currentStart, end: newEnd, span: newSpan }
                      );
                    }}
                    className="form-builder-grid-input"
                  />
                </div>
                <div className="form-builder-grid-control-group">
                  <label>Column Start (1-12):</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={selectedItem.gridPosition?.start || 1}
                    onChange={(e) => {
                      const newStart = Math.max(1, Math.min(12, parseInt(e.target.value) || 1));
                      const currentSpan = selectedItem.gridPosition?.span || 12;
                      const newEnd = newStart + currentSpan;
                      const newGridColumn = `${newStart} / ${newEnd}`;
                      handleUpdateGridPosition(
                        selectedItemIndex,
                        newGridColumn,
                        selectedItem.gridRow || 'auto',
                        { start: newStart, end: newEnd, span: currentSpan }
                      );
                    }}
                    className="form-builder-grid-input"
                  />
                </div>
                <div className="form-builder-grid-preview">
                  <div className="form-builder-grid-preview-label">Preview:</div>
                  <div className="form-builder-grid-preview-grid">
                    {Array.from({ length: 12 }).map((_, i) => {
                      const start = selectedItem.gridPosition?.start || 1;
                      const span = selectedItem.gridPosition?.span || 12;
                      const isActive = i >= start - 1 && i < start - 1 + span;
                      return (
                        <div
                          key={i}
                          className={`form-builder-grid-preview-cell ${isActive ? 'active' : ''}`}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
              <PropertyEditor
                item={selectedItem}
                onUpdate={(newProps) => handleUpdateProps(selectedItemIndex, newProps)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Clear Confirmation Modal */}
      <Modal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        title="Clear Form"
        primaryActionLabel="Clear"
        secondaryActionLabel="Cancel"
        onPrimaryAction={confirmClear}
        onSecondaryAction={() => setShowClearModal(false)}
      >
        <p>Are you sure you want to clear the form? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

// Enhanced Property Editor Component
const PropertyEditor = ({ item, onUpdate }) => {
  // eslint-disable-next-line no-unused-vars
  const { type, props } = item;
  const [localProps, setLocalProps] = useState(props);

  useEffect(() => {
    setLocalProps(props);
  }, [props]);

  const handleChange = (key, value) => {
    const newProps = { ...localProps, [key]: value };
    setLocalProps(newProps);
    onUpdate(newProps);
  };

  const handleArrayItemChange = (key, index, value) => {
    const newArray = [...localProps[key]];
    newArray[index] = value;
    handleChange(key, newArray);
  };

  const handleAddArrayItem = (key, defaultValue) => {
    const newArray = [...(localProps[key] || []), defaultValue];
    handleChange(key, newArray);
  };

  const handleRemoveArrayItem = (key, index) => {
    const newArray = localProps[key].filter((_, i) => i !== index);
    handleChange(key, newArray);
  };

  const renderPropertyInput = (key, value, label) => {
    // Special handling for options array (Select, Radio)
    if (key === 'options' && Array.isArray(value)) {
      return (
        <div key={key} className="form-builder-property">
          <label>{label || key}:</label>
          <div className="form-builder-array-editor">
            {value.map((option, index) => (
              <div key={index} className="form-builder-array-item">
                <input
                  type="text"
                  placeholder="Value"
                  value={option.value || ''}
                  onChange={(e) => {
                    const newOption = { ...option, value: e.target.value };
                    handleArrayItemChange(key, index, newOption);
                  }}
                  className="form-builder-array-input"
                />
                <input
                  type="text"
                  placeholder="Label"
                  value={option.label || ''}
                  onChange={(e) => {
                    const newOption = { ...option, label: e.target.value };
                    handleArrayItemChange(key, index, newOption);
                  }}
                  className="form-builder-array-input"
                />
                <button
                  type="button"
                  className="form-builder-array-remove"
                  onClick={() => handleRemoveArrayItem(key, index)}
                  title="Remove option"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="form-builder-array-add"
              onClick={() => handleAddArrayItem(key, { value: '', label: '' })}
            >
              + Add Option
            </button>
          </div>
        </div>
      );
    }

    // Regular array handling
    if (Array.isArray(value)) {
      return (
        <div key={key} className="form-builder-property">
          <label>{label || key}:</label>
          <div className="form-builder-array-editor">
            {value.map((item, index) => (
              <div key={index} className="form-builder-array-item">
                <input
                  type="text"
                  value={typeof item === 'object' ? JSON.stringify(item) : String(item)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleArrayItemChange(key, index, parsed);
                    } catch {
                      handleArrayItemChange(key, index, e.target.value);
                    }
                  }}
                  className="form-builder-array-input"
                />
                <button
                  type="button"
                  className="form-builder-array-remove"
                  onClick={() => handleRemoveArrayItem(key, index)}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="form-builder-array-add"
              onClick={() => handleAddArrayItem(key, '')}
            >
              + Add Item
            </button>
          </div>
        </div>
      );
    }

    // Object handling
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <div key={key} className="form-builder-property">
          <label>{label || key}:</label>
          <textarea
            value={JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange(key, parsed);
              } catch (err) {
                // Invalid JSON, ignore
              }
            }}
            rows={6}
            className="form-builder-json-input"
          />
        </div>
      );
    }

    // Boolean handling
    if (typeof value === 'boolean') {
      return (
        <div key={key} className="form-builder-property">
          <label>
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
            {label || key}
          </label>
        </div>
      );
    }

    // Number handling
    if (typeof value === 'number') {
      return (
        <div key={key} className="form-builder-property">
          <label>{label || key}:</label>
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(key, parseFloat(e.target.value) || 0)}
          />
        </div>
      );
    }

    // String handling
    return (
      <div key={key} className="form-builder-property">
        <label>{label || key}:</label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      </div>
    );
  };

  return (
    <div className="form-builder-properties-list">
      {Object.keys(localProps).map((key) => {
        const value = localProps[key];
        return renderPropertyInput(key, value);
      })}
    </div>
  );
};

export default FormBuilder;
